/**
 * Which website may show a clinic's booking form inside its frame (PA-12). One shared host serves
 * every clinic, so the browser's `frame-ancestors https:` lets any HTTPS site frame a booking
 * page (security-headers.ts); the clinic's own list of websites decides here instead. A framed
 * page shows the form only when every page around it is on the list, and posts its height only
 * to its parent's exact origin; any other frame sees a link to the clinic's booking page.
 */

/** What a page can see of the pages it is framed in. */
export interface EmbedContext {
  /** The page is inside another page's frame. */
  readonly framed: boolean;
  /**
   * Every ancestor's origin, nearest first, where the browser reports them
   * (location.ancestorOrigins); undefined where it does not.
   */
  readonly ancestorOrigins: readonly string[] | undefined;
  /** document.referrer: the website's origin, which the widget loader sends (strict-origin). */
  readonly referrer: string;
}

/** Where a booking page is shown. */
export type EmbedParent =
  /** On its own: the form shows and nothing is posted. */
  | { readonly framed: false }
  /** In a frame: the form shows, posting only to `origin`, or a link shows when it is null. */
  | { readonly framed: true; readonly origin: string | null };

export function readEmbedContext(view: Window = window): EmbedContext {
  let framed: boolean;
  try {
    framed = view.parent !== view;
  } catch {
    framed = true;
  }
  const ancestors = view.location.ancestorOrigins as DOMStringList | undefined;
  return {
    framed,
    ancestorOrigins: ancestors === undefined ? undefined : Array.from(ancestors),
    referrer: view.document.referrer,
  };
}

/**
 * The origins of the pages around a framed page, nearest first: every ancestor where the browser
 * lists them, else the https origin the referrer names (its parent only), else none.
 */
export function embeddingOrigins(context: EmbedContext): string[] {
  if (!context.framed) return [];
  if (context.ancestorOrigins !== undefined) return [...context.ancestorOrigins];
  try {
    const referrer = new URL(context.referrer);
    return referrer.protocol === "https:" ? [referrer.origin] : [];
  } catch {
    return [];
  }
}

/**
 * Where the page is shown, against the clinic's listed websites (`"any"`: the synthetic demo,
 * which has no list). An unknown ancestor, an unlisted one anywhere in the chain, or no ancestor
 * information at all gives a null origin: the form never shows there.
 */
export function embedParent(
  context: EmbedContext,
  websiteOrigins: readonly string[] | "any",
): EmbedParent {
  if (!context.framed) return { framed: false };
  const origins = embeddingOrigins(context);
  const parent = origins[0];
  // A sandboxed ancestor reports the origin "null", which can never be posted to.
  const listed =
    parent !== undefined &&
    parent !== "null" &&
    (websiteOrigins === "any" || origins.every((origin) => websiteOrigins.includes(origin)));
  return { framed: true, origin: listed ? parent : null };
}
